// The owner's percent override (spec §5.1: "derived from required steps; owner can override with
// a note"). Saving sets the percent and the note; "Use the steps again" clears the override and
// the API puts the percent back on the required steps ticked.
import { useId, useState, type FormEvent } from 'react';
import type { TaskDetail } from '@cis/api-client';
import { Modal } from '../../ui/modal.tsx';
import { DialogActions, ProblemText } from './dialog-parts.tsx';
import { baseFor, type TaskWriter } from './use-task-writer.ts';

const MAX_PERCENT = 100;
const NOTE_MAX = 500;

const edited = (d: TaskDetail): unknown => [d.state.percent, d.state.percentOverrideNote];

// One to three digits: matches "0", "40", "100"; rejects "12.5", "-1", "4O", "1000".
// https://regex101.com/?flavor=javascript&regex=%5E%5Cd%7B1%2C3%7D%24&testString=40%0A12.5%0A-1%0A1000
const WHOLE_NUMBER = /^\d{1,3}$/;

/** A whole number 0–100 typed in the field, or null. */
export function percentValue(text: string): number | null {
  if (!WHOLE_NUMBER.test(text.trim())) return null;
  const n = Number(text.trim());
  return n <= MAX_PERCENT ? n : null;
}

export function PercentDialog(props: {
  readonly detail: TaskDetail;
  readonly writer: TaskWriter;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { detail, writer } = props;
  const percentId = useId();
  const noteId = useId();
  const [opened] = useState(detail);
  const [percent, setPercent] = useState(String(opened.state.percent));
  const [note, setNote] = useState(opened.state.percentOverrideNote ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const overridden = opened.state.percentOverrideNote !== null;
  const send = async (value: { percent: number; note: string } | null): Promise<void> => {
    const write = { kind: 'edit', edit: { percentOverride: value } } as const;
    const outcome = await writer.send(write, baseFor(opened, detail, edited));
    if (outcome === 'ok' || outcome === 'conflict') props.onClose();
    if (outcome === 'failed') setProblem(writer.lastError());
  };
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const value = percentValue(percent);
    if (value === null) {
      setProblem('Type a whole number from 0 to 100.');
      return;
    }
    if (note.trim() === '') {
      setProblem('Say why the percent differs from the steps ticked.');
      return;
    }
    void send({ percent: value, note: note.trim() });
  };
  return (
    <Modal title="Change percent done" onClose={props.onClose} busy={writer.pending}>
      <p className="modal__body">
        The percent done normally follows the required steps ticked. Set it by hand only when the
        steps do not show the real progress, and say why.
      </p>
      <form className="field" onSubmit={onSubmit}>
        <label className="field__label" htmlFor={percentId}>
          Percent done (0 to 100)
        </label>
        <input
          id={percentId}
          className="field__input"
          inputMode="numeric"
          value={percent}
          onChange={(e) => setPercent(e.target.value)}
        />
        <label className="field__label" htmlFor={noteId}>
          Why
        </label>
        <textarea
          id={noteId}
          className="field__input"
          rows={3}
          maxLength={NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <ProblemText problem={problem} />
        <DialogActions writer={writer} onCancel={props.onClose} submitLabel="Save percent">
          {overridden && (
            <button
              type="button"
              className="btn btn--secondary"
              disabled={writer.pending}
              onClick={() => void send(null)}
            >
              Use the steps again
            </button>
          )}
        </DialogActions>
      </form>
    </Modal>
  );
}
